import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Card, Empty, Skeleton, Space, Tag, Typography } from 'antd';
import type { TeacherTodaySlot } from '@velora/contracts';
import { productApi } from '../../api';

/**
 * Teacher — Today. Lists my periods for today with register state; each
 * period jumps into the admin Attendance drawer (same data path).
 */
export function TeacherTodayPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const navigate = useNavigate();
  const today = new Date().toISOString().slice(0, 10);
  const q = useQuery({
    queryKey: ['teacher-today', institution, today],
    queryFn: () => productApi.getTeacherToday(institution, today),
    enabled: !!institution,
  });

  const dayLabel = new Date(today).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });

  if (q.isLoading) return <Skeleton active paragraph={{ rows: 6 }} />;
  if (!q.data) return <Alert type="error" showIcon message="Could not load today" />;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div>
        <Typography.Title level={3} style={{ margin: 0 }}>{dayLabel}</Typography.Title>
        {q.data.teacher ? (
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            Hi {q.data.teacher.name.split(' ')[0]} · {q.data.slots.length} period{q.data.slots.length === 1 ? '' : 's'} today
          </Typography.Text>
        ) : (
          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 8 }}
            message="Your login email isn't linked to a staff record here"
            description="Ask the admin to add you as staff with the same email so your periods show up."
          />
        )}
      </div>

      {q.data.teacher && q.data.slots.length === 0 && (
        <Empty description="No periods today — enjoy the break." style={{ padding: 20 }} />
      )}

      {q.data.slots.length > 0 && (
        <div style={{ display: 'grid', gap: 10 }}>
          {q.data.slots.map((s) => (
            <SlotCard
              key={s.slot_id}
              slot={s}
              onOpen={() => navigate(`/i/${institution}/teacher/attendance/${encodeURIComponent(s.grade)}/${encodeURIComponent(s.section)}`)}
            />
          ))}
        </div>
      )}

      {q.data.substitutions_covering.length > 0 && (
        <Card size="small" title={`Covering (${q.data.substitutions_covering.length})`}>
          {q.data.substitutions_covering.map((s) => (
            <div key={s.substitution_id} style={{ padding: '10px 0', borderTop: '1px solid #f1f1f4', fontSize: 13 }}>
              <b>{s.grade}{s.section ? `-${s.section}` : ''}</b> · P{s.period} · {s.subject_name}
              <div style={{ color: '#8a94a6', fontSize: 12, marginTop: 2 }}>
                original: {s.original_teacher_name ?? 'unassigned'}
              </div>
            </div>
          ))}
        </Card>
      )}

      {q.data.my_exams.length > 0 && (
        <Card size="small" title={`Marks to enter (${q.data.my_exams.length})`}>
          {q.data.my_exams.map((e) => (
            <div
              key={e.exam_subject_id}
              style={{ padding: '10px 0', borderTop: '1px solid #f1f1f4', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}
              onClick={() => navigate(`/i/${institution}/admin/exams/${e.exam_id}/marks?subject=${e.exam_subject_id}`)}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600 }}>{e.exam_name} · {e.subject_name}</div>
                <div style={{ color: '#8a94a6', fontSize: 12 }}>{e.grade}{e.section ? `-${e.section}` : ''} · {e.date}</div>
              </div>
              <Tag color={e.marks_entered === e.marks_expected ? 'success' : 'processing'}>
                {e.marks_entered} / {e.marks_expected}
              </Tag>
            </div>
          ))}
        </Card>
      )}
    </Space>
  );
}

function SlotCard({ slot, onOpen }: { slot: TeacherTodaySlot; onOpen: () => void }) {
  const stateColor = slot.register_state === 'submitted' ? 'success' : slot.register_state === 'open' ? 'processing' : 'warning';
  const stateLabel = slot.register_state === 'submitted' ? 'submitted' : slot.register_state === 'open' ? 'open' : 'not started';
  return (
    <Card size="small" hoverable onClick={onOpen}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{
          width: 44, height: 44, borderRadius: 8,
          background: '#eaf3fb', color: '#2f6ab0',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 700, fontSize: 15,
        }}>
          P{slot.period}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 15 }}>{slot.subject_name}</div>
          <div style={{ color: '#5b6478', fontSize: 12.5 }}>
            {slot.grade}{slot.section ? `-${slot.section}` : ''} · {slot.subject_code}
          </div>
        </div>
        <Tag color={stateColor}>{stateLabel}</Tag>
      </div>
      <Button
        size="small"
        type="primary"
        block
        style={{ marginTop: 10 }}
        onClick={(e) => { e.stopPropagation(); onOpen(); }}
      >
        Open register
      </Button>
    </Card>
  );
}
